import { useEffect, useState } from "react";
import { Check, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * VerifySocialPanel — admin Applications modal helper.
 *
 * Shows each social handle the applicant provided. For each row, admin clicks:
 *  1. "Open" — opens the public profile URL in a new tab
 *  2. "Mark verified" — records an audit entry via POST /admin/applications/{id}/verify-social
 *
 * Already-verified rows show a green check + admin email + timestamp pulled from `verifications`.
 */
export default function VerifySocialPanel({ application, onUpdated }) {
  const [verifications, setVerifications] = useState(application?.verifications || []);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    setVerifications(application?.verifications || []);
  }, [application?.id]);

  const handles = [
    { platform: "instagram", label: "Instagram", value: application?.instagram_handle, url: application?.instagram_handle ? `https://instagram.com/${(application.instagram_handle || "").replace(/^@/, "")}` : "" },
    { platform: "linkedin", label: "LinkedIn", value: application?.linkedin_url, url: application?.linkedin_url || "" },
    { platform: "youtube", label: "YouTube", value: application?.youtube_url, url: application?.youtube_url || "" },
    { platform: "twitter", label: "Twitter / X", value: application?.twitter_handle, url: application?.twitter_handle ? `https://twitter.com/${(application.twitter_handle || "").replace(/^@/, "")}` : "" },
    { platform: "website", label: "Brand website", value: application?.brand_website, url: application?.brand_website || "" },
  ].filter((h) => h.value);

  const verified = (platform) => verifications.find((v) => v.platform === platform);

  const mark = async (platform) => {
    try {
      setBusy(platform);
      const r = await api.post(`/admin/applications/${application.id}/verify-social`, { platform });
      setVerifications((v) => [r.data.verification, ...v]);
      toast.success(`${platform} marked verified`);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  if (handles.length === 0) return null;

  return (
    <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid="verify-social-panel">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-wider text-zinc-500">Profile verification</p>
        <p className="text-[10px] text-zinc-600">{verifications.length} verified</p>
      </div>
      <ul className="mt-3 space-y-2">
        {handles.map((h) => {
          const v = verified(h.platform);
          return (
            <li key={h.platform} data-testid={`verify-row-${h.platform}`} className="flex flex-wrap items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2">
              <span className="w-20 shrink-0 text-xs font-bold uppercase tracking-wide text-zinc-400">{h.label}</span>
              <span className="flex-1 truncate font-mono text-xs text-zinc-300">{h.value}</span>
              <a href={h.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded border border-zinc-800 px-2 py-1 text-[11px] text-zinc-200 hover:border-[#A855F7]/60 hover:text-white">
                Open <ExternalLink className="h-3 w-3" />
              </a>
              {v ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-[#A855F7]/50 bg-[#A855F7]/15 px-2 py-1 text-[10px] font-bold text-[#E9D5FF]" title={`by ${v.verified_by} at ${new Date(v.verified_at).toLocaleString()}`}>
                  <Check className="h-3 w-3" /> Verified
                </span>
              ) : (
                <button
                  onClick={() => mark(h.platform)}
                  disabled={busy === h.platform}
                  data-testid={`verify-btn-${h.platform}`}
                  className="inline-flex items-center gap-1 rounded bg-[#A855F7] px-2 py-1 text-[11px] font-bold text-white hover:bg-[#9333EA] disabled:opacity-50"
                >
                  {busy === h.platform ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Mark verified
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <p className="mt-2 text-[10px] text-zinc-600">
        Click <strong>Open</strong> to validate the profile (real account, plausible follower count, no obvious bots), then <strong>Mark verified</strong> to log it. Audit trail is permanent.
      </p>
    </div>
  );
}
